<script>
import { mapActions, mapGetters } from 'vuex'

import { filterLoginData } from './utils'
import { LOGIN_METHODS } from './constants'
import LoginForm from './components/LoginForm.vue'
import AuthFormVerificationCode from './components/AuthFormVerificationCode.vue'
import LoginForgotPasswordForm from './components/LoginForgotPasswordForm.vue'
import LoginResetPasswordSuccess from './components/LoginResetPasswordSuccess.vue'
import locator from '@/service-locator'
import { asyncRoutes } from '@/router'
import { setToken } from '@/utils/auth'
import MerchantUserApi from '@/api/merchant/users'
import LoginLayout from '@/layout/login/Login.vue'
import { setOnboardingSession } from '@/utils/onboarding-session'
import { handleRedirectUserToOnboarding } from '@/utils/onboarding'
import { getMarketingData } from '@/utils/query-string'

const STEPS = {
  login: 'input_email',
  otp: 'verify_otp',
  forgot_password: 'forgot_password',
  sent_reset_password_success: 'sent_reset_password_success',
}

const OTP_LENGTH = 6

export default {
  name: 'LoginPage',
  components: {
    LoginLayout,
    LoginForm,
    AuthFormVerificationCode,
    LoginForgotPasswordForm,
    LoginResetPasswordSuccess,
  },
  data() {
    return {
      step: STEPS.login,
      otpTimer: 60,
      submitting: false,
      resetPasswordSubmitting: false,
      submittedEmail: '',
      submittedCountryCallingCode: '',
      submittedPhoneNumber: '',
      submittingGoogleSso: false,
      submittingVerificationCode: false,
      submittingResendCode: false,
      redirectingToOnboarding: false,
      loginMethod: '',
      errorMessages: {
        resetPassword: '',
        verificationCode: '',
        resendVerificationCode: '',
      },
      isLocked: false,
    }
  },
  computed: {
    ...mapGetters(['currentLocale']),
    ...mapGetters('userProfile', ['userPermissions', 'selectedMerchant']),
    ...mapGetters('permission', ['sessionExpired']),
    STEPS() {
      return STEPS
    },
    isProduction() {
      return locator.config.isProduction
    },
    userPermissionCodes() {
      return this.userPermissions.map((permission) => permission.code)
    },
    isLoginFormStep() {
      return this.step === STEPS.login
    },
    isForgotPasswordStep() {
      return this.step === STEPS.forgot_password
    },
    isShowResend() {
      return !this.isLocked
    },
    shouldDisableInput() {
      return this.isLocked
    },
    isUsingPhoneNumber() {
      return this.loginMethod === LOGIN_METHODS.phone_number
    },
    submittedIdentify() {
      if (this.isUsingPhoneNumber) {
        return `+ ${this.submittedCountryCallingCode} ${this.submittedPhoneNumber}`
      }

      return this.submittedEmail
    },
    dialogDescription() {
      if (this.isUsingPhoneNumber) {
        return this.$t('otp_dialog_description_phone_number')
      }

      return this.$t('otp_dialog_description')
    },
    dialogDescription2() {
      if (this.isUsingPhoneNumber) {
        return this.$t('otp_dialog_description_phone_number_2')
      }

      return ''
    },
    dialogDescriptionChangeLoginType() {
      if (this.isUsingPhoneNumber) {
        return this.$t('otp_dialog_description_change_phone_number')
      }

      return this.$t('otp_dialog_description_change_email')
    },
  },
  watch: {
    step() {
      this.resetErrorMessages()
    },
    errorMessages: {
      handler(newVal) {
        this.checkLockedStatus(newVal?.verificationCode)
      },
      deep: true,
    },
  },
  async created() {
    // reset metaFields when logout
    await this.resetProfile()

    await this.completeSsoLoginIfNeeded()

    locator.chatbot.setChatMetaFields({
      merchant_id: '',
      merchant_name: '',
      merchant_email: '',
      merchant_tier: '',
      merchant_code: '',
      merchant_platform: '',
      merchant_authenticated_status: false,
    })
  },
  methods: {
    ...mapActions('api', ['verifyOtp2FA', 'resendOtp2FA']),
    ...mapActions('profile', ['resetProfile']),
    ...mapActions('user', ['fetchSsoSettings', 'loginSso']),
    changeStep(step) {
      this.step = step
    },
    checkLockedStatus(errorCode) {
      this.isLocked = [
        '2fa_login_exception.otp_incorrect_user_disabled',
        '2fa_login_exception.otp_session_expired',
        'merchant.user_locked',
      ].includes(errorCode)
    },
    assignSubmittedEmail(email) {
      this.submittedEmail = email
    },
    assignSubmittedPhoneNumber({ countryCallingCode, phoneNumber }) {
      this.submittedCountryCallingCode = countryCallingCode
      this.submittedPhoneNumber = phoneNumber
    },
    validateEmail(email) {
      if (!email) {
        return 'email_required'
      }

      if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
        return 'email_not_valid'
      }

      return ''
    },
    validateResetPasswordEmail(email) {
      const error = this.validateEmail(email)

      return error ? this.$t(error) : ''
    },
    async accessibleRoutes() {
      const accessibleRoutes = []
      const visibleRoutes = await this.visibleRoutes()
      visibleRoutes.map((route) => {
        if (route.meta.permissions) {
          // return all routes the user has access to
          route.meta.permissions.find((permission) => {
            if (this.userPermissionCodes.includes(permission)) {
              accessibleRoutes.push(route.path)
            }
          })
        }

        // and include routes that don't have permissions to access
        if (!route.meta.permissions) {
          accessibleRoutes.push(route.path)
        }
      })
      return accessibleRoutes || ['/']
    },
    async visibleRoutes() {
      const res = []
      for (const route of asyncRoutes) {
        if (route.hidden || !route.children) {
          continue
        }

        for (const item of route.children) {
          if (!item.hidden) {
            res.push(item)
          }
        }
      }

      return res
    },
    async handleResend() {
      const dataToBeSent = {
        channel: 'sms',
        email: this.submittedEmail,
        phoneNumber: this.submittedPhoneNumber,
        countryCode: this.submittedCountryCallingCode,
      }
      const filteredData = filterLoginData(dataToBeSent, this.loginMethod)
      const response = await this.resendOtp2FA(filteredData)

      this.startTimer()

      if (response.error) {
        this.errorMessages.resendVerificationCode = response.error?.message
      }
    },
    startTimer() {
      if (this.timerRef !== null) {
        clearInterval(this.timerRef)
      }

      this.otpTimer = 60
      this.timerRef = setInterval(() => {
        if (this.otpTimer !== 0) {
          this.otpTimer = this.otpTimer - 1
        }
      }, 1000)
    },
    async handleSubmitLogin(formData) {
      if (!this.isLoginFormStep || this.submitting) {
        return
      }

      try {
        this.submitting = true

        const loginByEmail = !!formData.email
        const userCredentials = {
          password: formData.password,
        }

        if (loginByEmail) {
          this.loginMethod = LOGIN_METHODS.email

          this.assignSubmittedEmail(formData.email)

          userCredentials.email = formData.email
        } else {
          this.loginMethod = LOGIN_METHODS.phone_number

          this.assignSubmittedPhoneNumber({
            countryCallingCode: formData.countryCallingCode,
            phoneNumber: formData.phoneNumber,
          })

          userCredentials.phoneNumber = formData.phoneNumber
          userCredentials.countryCallingCode = formData.countryCallingCode
        }

        this.$logger.identify({
          email: this.submittedEmail,
          phoneNumber: this.submittedPhoneNumber,
        })

        this.$logger.log('user_submit_login', {
          loginMethod: this.loginMethod,
        })

        // workaround to make sure cached data are cleared
        await this.$store.dispatch('user/logout')

        await this.$recaptchaLoaded()

        // TODO: We should refactor this to be more generic
        const reCaptchaToken = await this.$recaptcha('login')
        const response = await this.$store.dispatch('user/login', {
          reCaptchaToken,
          userCredentials,
          utmParams: {
            ...getMarketingData(),
          },
        })

        // TODO: We should refactor this
        if (response.status === 200) {
          this.$logger.log('user_submit_login_success', {
            loginMethod: this.loginMethod,
          })

          if (response.data.token) {
            setToken(response.data.token)

            if (
              response.data?.onboardingVersion === 'v3' &&
              response.data.isOnboarding
            ) {
              window.location.href = `${window.location.origin}/onboarding/signup`
              return
            }

            this.$track.event('login_successful')

            await this.redirectAfterLoggedIn()
          } else {
            this.changeStep(STEPS.otp)

            this.$track.event('login_page_post_login_otp_pop_up_appears')
          }
        }

        this.submitting = false
      } catch (error) {
        this.submitting = false

        this.$logger.log('user_submit_login_error', {
          loginMethod: this.loginMethod,
          errorMessage: error?.message || 'Unknown',
        })

        this.$track.event('login_failed')
      }
    },
    async handleSsoLogin(provider) {
      this.loading = true

      try {
        this.$track.event('login_page_sign_in_with_google_clicked')

        const response = await this.fetchSsoSettings({ provider })

        if (response.data.ssoAuthUrl) {
          window.location.href = response.data.ssoAuthUrl
        }
      } finally {
        this.loading = false
      }
    },
    openOtpDialog() {
      this.otpDialogVisible = true
    },
    cleanSsoQueryParams() {
      // Vue routes are started after #
      // Need to clean anything added by SSO provider before #

      const currentUrl = window.location.href
      const splitUrl = currentUrl.split('#/')
      window.history.replaceState({}, null, '/#/' + splitUrl[1])
    },
    async completeSsoLoginIfNeeded() {
      const queryString = window.location.search
      const urlParams = new URLSearchParams(queryString)
      const urlSegments = window.location.pathname.split('/')
      if (!urlParams.has('code')) {
        return
      }
      if (urlSegments[1] !== 'sso' && urlSegments[2] !== 'complete') {
        return
      }

      const provider = urlSegments[3]
      const authCode = urlParams.get('code')
      try {
        this.submittingGoogleSso = true

        await this.loginSso({
          provider,
          authCode,
        })

        this.cleanSsoQueryParams()

        this.$track.event('login_successful')

        await this.redirectAfterLoggedIn()
      } catch (_) {
        this.$track.event('login_failed')
      } finally {
        this.submittingGoogleSso = false
      }
    },
    // TODO: We should refactor this to be more generic
    async redirectAfterLoggedIn() {
      const data = await this.$store.dispatch(
        'userProfile/getMerchantUserProfile'
      )

      if (data?.version === 'v2' && locator.config.onboardingPageUrl) {
        let onboardingUrl = locator.config.onboardingPageUrl
        if (data?.currentStep) {
          onboardingUrl += `?step=${data.currentStep}`
        }
        window.location.href = onboardingUrl
        return
      }

      if (data.role === 'MERCHANT_KYC_USER') {
        this.$router.push('/kyc')

        return
      }

      const onboardingId = this.$store.state.userProfile.onboardingId
      if (onboardingId && data.role === 'MERCHANT_ONBOARDING_USER') {
        await this.handleOnboardingUser(onboardingId)
      } else {
        const accessibleRoutes = await this.accessibleRoutes()
        if (
          this.redirect &&
          accessibleRoutes &&
          accessibleRoutes.length !== 0
        ) {
          // find if redirect route is one of the accessible routes to the user
          if (accessibleRoutes.includes(this.redirect)) {
            const { data } = await MerchantUserApi.getMerchantUserProfile()

            locator.chatbot.setChatMetaFields({
              merchant_authenticated_status: true,
              merchant_email: data.email || '',
            })

            locator.tracking.identify(this.selectedMerchant, {
              email: data.email || '',
            })

            this.$router.push({
              path:
                data?.status !== 'active'
                  ? this.redirect || '/'
                  : '/dashboard/home',
              query: this.otherQuery,
            })
            return true
          }

          // go to first accessible route
          this.$router.push(accessibleRoutes[0])
        } else {
          // otherwise just redirect to the main route
          this.$router.push('/')
        }
      }
    },
    async handleSubmitResetPassword(email) {
      try {
        this.$track.event('login_page_send_password_reset_link_click')

        this.assignSubmittedEmail(email)

        const errorKey = this.validateResetPasswordEmail(email)

        if (errorKey) {
          this.errorMessages.resetPassword = this.$t(errorKey)

          return
        }

        this.resetPasswordSubmitting = true

        await this.$recaptchaLoaded()

        const res = await this.$store.dispatch('user/forgotPassword', {
          email,
        })

        this.handleResponseResetPassword(res)

        this.resetPasswordSubmitting = false

        this.$track.event('login_page_send_password_reset_link_successful')
      } catch (error) {
        this.resetPasswordSubmitting = false

        const errorKey =
          error?.response?.data?.message || 'Something went wrong'

        this.errorMessages.resetPassword = this.$te(errorKey)
          ? this.$t(errorKey)
          : errorKey

        this.$track.event('login_page_send_password_reset_link_failed')
      }
    },
    handleResponseResetPassword(response) {
      if (response.status >= 400) {
        this.errorMessages.resetPassword = response?.data?.message
      } else {
        this.handleSubmitResetPasswordSuccess()
      }
    },
    async handleOnboardingUser(onboardingId) {
      // step based redirection will be done after kyc
      setOnboardingSession(onboardingId)

      const userToken = this.$store.state.user.token
      const userProfile = this.$store.getters['userProfile/profile']
      const apiVersion = userProfile.version

      handleRedirectUserToOnboarding(userToken, this.$router, { apiVersion })
    },
    resetErrorMessages() {
      this.errorMessages = {
        resetPassword: '',
      }
    },
    handleBackToInput() {
      this.step = STEPS.login
    },
    changeToForgotPasswordScreen() {
      this.$track.event('login_page_forget_password_clicked')

      this.changeStep(STEPS.forgot_password)
    },
    handleSubmitResetPasswordSuccess() {
      this.changeStep(STEPS.sent_reset_password_success)
    },
    // TODO: Go to onboarding in old flow. Need to refactor and Partner Onboarding implement signup flow fully
    handleGoToSignupScreen() {
      if (
        this.$abtProvider.isFeatureEnabled('merchant_onboarding_revamp_enabled')
      ) {
        window.location.href = `${locator.config.onboardingPageUrl}/signup`
      } else {
        window.location.href = `${window.location.origin}/onboarding/signup`
      }
    },
    handleBackToLogin() {
      this.changeStep(STEPS.login)
    },
    async handleSubmitVerificationCode({ verificationCode }) {
      if (verificationCode.length !== OTP_LENGTH) {
        this.errorMessages.verificationCode = 'otp_invalid'

        return
      }

      if (!this.isShowResend) {
        return
      }

      try {
        this.submittingVerificationCode = true

        const dataToBeSent = {
          channel: 'sms',
          otp: verificationCode,
          email: this.submittedEmail,
          countryCode: this.submittedCountryCallingCode,
          phoneNumber: this.submittedPhoneNumber,
        }
        const filteredData = filterLoginData(dataToBeSent, this.loginMethod)

        const response = await this.verifyOtp2FA(filteredData)

        this.submittingVerificationCode = false

        if (response.error) {
          this.errorMessages.verificationCode = response.error?.message
        } else {
          this.$track.event('login_successful')

          await this.redirectAfterLoggedIn()
        }
      } catch (error) {
        const errorData = error?.response?.data || ''

        this.errorMessages.verificationCode =
          errorData?.error?.message || error?.message

        this.$track.event('login_failed')
      } finally {
        this.submittingVerificationCode = false

        this.checkLockedStatus(this.errorMessages.verificationCode)
      }
    },
  },
}
</script>

<template>
  <LoginLayout>
    <LoginForm
      v-if="step === STEPS.login"
      :submitting="submitting"
      :submitting-google-sso="submittingGoogleSso"
      :redirecting-to-onboarding="redirectingToOnboarding"
      @change:step="changeStep"
      @login:email="handleSubmitLogin"
      @login:phone-number="handleSubmitLogin"
      @login:sso="handleSsoLogin"
      @click:forgot-password="changeToForgotPasswordScreen"
      @click:signup="handleGoToSignupScreen"
    />
    <AuthFormVerificationCode
      v-else-if="step === STEPS.otp"
      :title="$t('otp_dialog_title')"
      :resend-visible="isShowResend"
      :disabled-input="shouldDisableInput"
      :otp-timer="otpTimer"
      :error-key="errorMessages.verificationCode"
      :error-key-resend="errorMessages.resendVerificationCode"
      :submitting="submittingVerificationCode"
      :submitting-resend="submittingResendCode"
      @mounted="startTimer"
      @resend="handleResend"
      @submit:verification-code="handleSubmitVerificationCode"
    >
      <template slot="description">
        {{ dialogDescription }}
        <span class="tamara-login-verification-code__description__email">
          {{ submittedIdentify }},
        </span>
        {{ dialogDescription2 }}
        {{ $t('otp_dialog_description_valid_time') }}
        <span
          class="tamara-login-verification-code__description__change-email"
          @click="handleBackToInput"
        >
          {{ dialogDescriptionChangeLoginType }}
        </span>
      </template>
    </AuthFormVerificationCode>
    <LoginForgotPasswordForm
      v-else-if="isForgotPasswordStep"
      :submitting="resetPasswordSubmitting"
      :error-message="errorMessages.resetPassword"
      @submit="handleSubmitResetPassword"
    />
    <LoginResetPasswordSuccess
      v-else-if="step === STEPS.sent_reset_password_success"
      :submitted-email="submittedEmail"
      @back:login="handleBackToLogin"
    />
  </LoginLayout>
</template>

<style lang="scss" scoped>
@import '@/styles/lavender/design.scss';

.tamara-login-verification-code {
  &__description {
    &__email {
      display: inline-block;
      color: var(--tamara-color-link);
    }

    &__change-email {
      color: var(--tamara-color-primary);
      cursor: pointer;

      &:hover {
        opacity: 0.8;
      }
    }
  }
}
</style>
